import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { ProjectCard } from '../lib/types';
import { PROJECT_STATUS_LABELS,
  FINISHED_PROJECT_STATUSES,
} from '../lib/types';
import { PriorityBadge, ProjectStatusBadge } from '../components/Badges';
import { formatDate } from '../lib/dates';
import { BoardPage } from './BoardPage';

/** Remembers the last project so the page opens where somebody left it. */
const LAST = 'lucentra.projectManagement.last';

/**
 * Every project somebody can reach, and its board, on one page.
 *
 * WHY IT EXISTS. The kanban was three clicks deep — Companies, then the company, then the
 * project, then its board — which is a fine path the first time and a tax every time after.
 * Jaco asked for the boards to be brought out; this is that: pick a project and the board is
 * under it, with the same view tabs it has always had.
 *
 * A CLIENT SEES THIS TOO, and sees exactly what they see everywhere else: the API returns the
 * projects shared with their company and nothing more. The page gathers things up, which is
 * precisely where a second, weaker visibility rule would go unnoticed — so it has none of its
 * own, and the board it renders re-checks access when it loads.
 *
 * The chosen project lives in the ADDRESS, not only in state, so a board somebody is looking
 * at can be sent to a colleague and opens on the same thing.
 */
export function ProjectManagementPage() {
  /* A page that IS a table: it gets the window, not the reading column. */
  const [projects, setProjects] = useState<ProjectCard[] | null>(null);
  const [showFinished, setShowFinished] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [find, setFind] = useState('');
  const [params, setParams] = useSearchParams();

  const chosen = params.get('project');

  const load = useCallback(async () => {
    try {
      setProjects(await api.get<ProjectCard[]>('/projects'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your projects');
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Opens where it was left — ONCE, on arrival, and only when the address does not already say.
   *
   * The `resumed` guard is the whole point. Without it this ran again the moment somebody
   * pressed "All projects": the address cleared, the effect saw no project, and it put the
   * remembered one straight back — a button that could not do the one thing it said.
   *
   * The remembered id is checked against the list rather than trusted: a project somebody has
   * since lost access to would otherwise open a board that answers 404.
   */
  const resumed = useRef(false);
  useEffect(() => {
    if (resumed.current || chosen || !projects || projects.length === 0) return;
    resumed.current = true;

    try {
      const remembered = localStorage.getItem(LAST);
      if (remembered && projects.some((project) => project.id === remembered)) {
        setParams({ project: remembered }, { replace: true });
      }
    } catch {
      /* private browsing */
    }
  }, [chosen, projects, setParams]);

  function choose(projectId: string | null) {
    if (projectId) {
      setParams({ project: projectId });
      try {
        localStorage.setItem(LAST, projectId);
      } catch {
        /* private browsing */
      }
    } else {
      setParams({});
    }
  }

  /**
   * HOW MANY ARE FINISHED, and therefore out of the way.
   *
   * Counted over everything rather than over what the search left, so the toggle does not
   * change its number as somebody types — a count that moves while you read it is a count
   * nobody trusts.
   */
  const finishedCount = (projects ?? []).filter((project) => FINISHED_PROJECT_STATUSES.has(project.status)).length;

  /** Grouped by company, because that is how anybody with more than one thinks about them. */
  const grouped = useMemo(() => {
    const query = find.trim().toLowerCase();
    const kept = (projects ?? [])
      /*
        DELIVERED AND ARCHIVED DROP OUT, which is the whole reason this page got hard to use.
        The list only ever grew: everything ever finished stayed in it at full size, and the
        thing somebody actually came for was three screens down. Hidden rather than removed —
        the toggle is right there, and a finished project is still a project.
      */
      .filter((project) => showFinished || !FINISHED_PROJECT_STATUSES.has(project.status))
      .filter((project) =>
        query
          ? [project.name, project.code, project.company.name, project.company.code]
              .join('\n')
              .toLowerCase()
              .includes(query)
          : true,
      );

    const byCompany = new Map<string, { name: string; id: string; projects: ProjectCard[] }>();
    for (const project of kept) {
      const entry = byCompany.get(project.company.id) ?? {
        id: project.company.id,
        name: project.company.name,
        projects: [],
      };
      entry.projects.push(project);
      byCompany.set(project.company.id, entry);
    }

    return [...byCompany.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [projects, find, showFinished]);

  const current = projects?.find((project) => project.id === chosen) ?? null;

  if (error) return <div className="callout">{error}</div>;
  if (!projects) return <div className="empty">Loading…</div>;

  if (projects.length === 0) {
    return (
      <div className="card">
        <div className="empty">
          <h3>No projects yet</h3>
          <p>Nothing has been shared with you. An administrator grants access per company.</p>
        </div>
      </div>
    );
  }

  /**
   * A project is chosen: its board, under a strip that names it and gets you out again.
   *
   * The board brings its own heading, breadcrumb and view tabs — kanban, table, tree, Gantt —
   * so nothing is reimplemented here. This page's job ends at choosing.
   */
  if (current) {
    return (
      <>
        <div className="picker-strip">
          {/* Out first, on the left, where a back control is looked for. */}
          <button type="button" className="btn btn-quiet btn-sm" onClick={() => choose(null)}>
            ‹ All projects
          </button>

          <select
            className="input-sm picker-select"
            aria-label="Project"
            value={current.id}
            onChange={(event) => choose(event.target.value)}
          >
            {grouped.map((company) => (
              <optgroup key={company.id} label={company.name}>
                {company.projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.code} — {project.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          {/*
            The same three numbers as the card it was chosen from, so switching projects tells
            you what you have walked into without reading the board first.
          */}
          <span className="picker-counts">
            <span>
              <strong>{current.open}</strong> open
            </span>
            <span className={current.overdue ? 'is-bad' : undefined}>
              <strong>{current.overdue}</strong> overdue
            </span>
            <span className={current.blocked ? 'is-bad' : undefined}>
              <strong>{current.blocked}</strong> blocked
            </span>
          </span>

          <span className="grow" />

          {/*
            `from=pm` is what lets those pages offer a way back in here. Without it somebody
            who stepped off the board landed on a page whose only exit was the company
            dashboard — in one door and out another.
          */}
          <Link to={`/projects/${current.id}?from=pm`} className="btn btn-quiet btn-sm">
            Overview
          </Link>
          <Link to={`/projects/${current.id}/roadmap?from=pm`} className="btn btn-quiet btn-sm">
            Roadmap
          </Link>
        </div>

        <BoardPage projectId={current.id} />
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Delivery</div>
          <h1>Project management</h1>
          <p className="subtitle">
            Every project you can reach, and its board one click away rather than three.
          </p>
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          {/*
            OFFERED ONLY WHEN THERE IS SOMETHING BEHIND IT. A switch that reveals nothing is a
            control somebody presses twice to find out it does nothing, and on a fresh account
            there are no finished projects at all.
          */}
          {finishedCount > 0 && (
            <button
              type="button"
              className={showFinished ? 'btn btn-sm' : 'btn btn-quiet btn-sm'}
              onClick={() => setShowFinished((on) => !on)}
              title={
                showFinished
                  ? 'Hide projects that have been delivered or archived'
                  : 'Show projects that have been delivered or archived'
              }
            >
              {showFinished ? 'Hide finished' : `Show ${finishedCount} finished`}
            </button>
          )}
          <input
            type="search"
            className="input-sm"
            style={{ minWidth: 220 }}
            value={find}
            placeholder="Find a project"
            onChange={(event) => setFind(event.target.value)}
          />
        </div>
      </div>

      {grouped.length === 0 ? (
        <div className="card">
          <div className="empty">
            {/*
              Two ways to arrive at an empty page now, and they want different answers. Saying
              "nothing matches" when somebody has simply finished everything would send them
              looking for a typo that is not there.
            */}
            {find.trim() ? (
              <>
                <h3>Nothing matches “{find.trim()}”</h3>
                <p>
                  None of your {projects.length} projects match that
                  {!showFinished && finishedCount > 0 ? `, and ${finishedCount} finished ones are hidden` : ''}.
                </p>
              </>
            ) : (
              <>
                <h3>Nothing open</h3>
                <p>
                  All {finishedCount} of your projects have been delivered or archived. Show them with the
                  button above.
                </p>
              </>
            )}
          </div>
        </div>
      ) : (
        grouped.map((company) => (
          <div key={company.id} className="card mb-2">
            <div className="card-head">
              <h2>{company.name}</h2>
              <span className="dim" style={{ fontSize: 12 }}>
                {company.projects.length} project{company.projects.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="card-pad">
              <div className="project-grid">
                {company.projects.map((project) => (
                  /*
                    The whole card opens the board, because that is what anybody came for.
                    The links inside it stop the card's own click so they still go where they
                    say — a card that swallowed them would make the overview unreachable.
                  */
                  <button key={project.id} type="button" className="project-card" onClick={() => choose(project.id)}>
                    <span className="row" style={{ gap: 8 }}>
                      <span className="key">{project.code}</span>
                      <span className="project-card-name">{project.name}</span>
                    </span>

                    <span className="row wrap" style={{ gap: 5, marginTop: 7 }}>
                      <ProjectStatusBadge status={project.status} />
                      <PriorityBadge priority={project.priority} />
                      {project.restricted && <span className="badge">restricted</span>}
                      {project.sharedWithClient && <span className="badge badge-info">shared</span>}
                    </span>

                    <span className="project-card-counts">
                      <span>
                        <strong>{project.open}</strong> open
                      </span>
                      <span className={project.overdue ? 'is-bad' : undefined}>
                        <strong>{project.overdue}</strong> overdue
                      </span>
                      <span className={project.blocked ? 'is-bad' : undefined}>
                        <strong>{project.blocked}</strong> blocked
                      </span>
                    </span>

                    <span className="project-card-foot dim">
                      {project.targetDate ? `Target ${formatDate(project.targetDate)}` : 'No target date'}
                      {' · '}
                      {PROJECT_STATUS_LABELS[project.status]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))
      )}
    </>
  );
}
